import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareText, UploadCloud } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-onyx border border-graphite px-4 py-3.5 text-sm text-ivory placeholder:text-silver/40 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt transition-colors duration-300";
const labelCls = "block font-accent text-[10px] tracking-[0.25em] uppercase text-silver/60 mb-2";

const Field = ({ label, id, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>{label}</label>
    {children}
  </div>
);

const todayLocal = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export const RequestForm = ({ serviceInterest }) => {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "",
    vehicle_condition: "Not Sure",
    service_interest: serviceInterest || "Not Sure",
    preferred_day: "", service_location: "Home", notes: "",
  });
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);
  const minPreferredDay = todayLocal();

  useEffect(() => {
    if (serviceInterest) setForm((f) => ({ ...f, service_interest: serviceInterest }));
  }, [serviceInterest]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      photos.forEach((p) => data.append("photos", p));
      await axios.post(`${API}/requests`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      toast.success("Request received. DARK will reach out shortly.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <section id="request" data-testid="request-section" className="py-28 sm:py-40 px-5 sm:px-8 border-t border-graphite/60 bg-onyx/20">
        <div className="max-w-2xl mx-auto text-center" data-testid="request-success">
          <p className="font-accent text-xs tracking-[0.35em] uppercase text-volt mb-6">Request Received</p>
          <h2 className="font-display text-4xl sm:text-5xl text-ivory leading-tight">Your vehicle is on our radar.</h2>
          <p className="text-silver/70 text-base mt-6 leading-relaxed">
            We review every request personally and respond with the right level of care for your vehicle.
          </p>
          <a
            data-testid="text-dark-now-success"
            href="sms:+19133885449"
            className="inline-flex items-center gap-3 mt-10 font-accent text-xs tracking-[0.25em] uppercase border border-silver/25 text-silver px-8 py-4 hover:border-volt hover:text-ivory transition-colors duration-300"
          >
            <MessageSquareText className="w-4 h-4" /> Text DARK Now
          </a>
        </div>
      </section>
    );
  }

  return (
    <section id="request" data-testid="request-section" className="py-28 sm:py-40 px-5 sm:px-8 border-t border-graphite/60 bg-onyx/20">
      <div className="max-w-3xl mx-auto">
        <SectionHead
          index="06"
          overline="Request Service"
          title="Request Service"
          copy="Tell us about your vehicle. We'll recommend the right level of care."
        />
        <Reveal delay={0.15}>
          <form onSubmit={onSubmit} data-testid="request-form" className="mt-16 grid sm:grid-cols-2 gap-6">
            <Field label="Full Name" id="full_name">
              <input id="full_name" data-testid="input-full-name" required className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your name" autoComplete="name" />
            </Field>
            <Field label="Phone" id="phone">
              <input id="phone" data-testid="input-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Your phone number" autoComplete="tel" />
            </Field>
            <Field label="Email" id="email">
              <input id="email" data-testid="input-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" autoComplete="email" />
            </Field>
            <Field label="Preferred Day" id="preferred_day">
              <input id="preferred_day" data-testid="input-preferred-day" type="date" min={minPreferredDay} className={`${inputCls} [color-scheme:dark]`} value={form.preferred_day} onChange={set("preferred_day")} />
            </Field>
            <Field label="Vehicle Make" id="vehicle_make">
              <input id="vehicle_make" data-testid="input-vehicle-make" required className={inputCls} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Tesla" />
            </Field>
            <Field label="Vehicle Model" id="vehicle_model">
              <input id="vehicle_model" data-testid="input-vehicle-model" required className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Model 3" />
            </Field>
            <Field label="Vehicle Year" id="vehicle_year">
              <input id="vehicle_year" data-testid="input-vehicle-year" required className={inputCls} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2023" inputMode="numeric" />
            </Field>
            <Field label="Vehicle Condition" id="vehicle_condition">
              <select id="vehicle_condition" data-testid="select-vehicle-condition" className={inputCls} value={form.vehicle_condition} onChange={set("vehicle_condition")}>
                <option>Needs Restoration</option>
                <option>Already Maintained</option>
                <option>Not Sure</option>
              </select>
            </Field>
            <Field label="Service Interest" id="service_interest">
              <select id="service_interest" data-testid="select-service-interest" className={inputCls} value={form.service_interest} onChange={set("service_interest")}>
                <option>THE ENTRY</option>
                <option>THE WITHIN</option>
                <option>THE STANDARD</option>
                <option>PRIVATE CARE</option>
                <option>BLACK ACCESS</option>
                <option>Not Sure</option>
              </select>
            </Field>
            <Field label="Service Location" id="service_location">
              <select id="service_location" data-testid="select-service-location" className={inputCls} value={form.service_location} onChange={set("service_location")}>
                <option>Home</option>
                <option>Workplace</option>
                <option>Other</option>
              </select>
            </Field>
            <div className="sm:col-span-2">
              <label htmlFor="photos" className={labelCls}>Photo Upload</label>
              <button
                type="button"
                data-testid="upload-photos-button"
                onClick={() => fileRef.current?.click()}
                className="w-full border border-dashed border-graphite hover:border-volt/60 bg-onyx/50 px-4 py-8 flex flex-col items-center gap-3 transition-colors duration-300"
              >
                <UploadCloud className="w-5 h-5 text-volt" strokeWidth={1.5} />
                <span className="text-sm text-silver/70">
                  {photos.length ? `${photos.length} photo${photos.length > 1 ? "s" : ""} selected` : "Add up to 4 photos of your vehicle"}
                </span>
              </button>
              <input
                ref={fileRef}
                id="photos"
                data-testid="input-photos"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 4))}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="notes" className={labelCls}>Additional Notes</label>
              <textarea id="notes" data-testid="input-notes" rows={4} className={inputCls} value={form.notes} onChange={set("notes")} placeholder="Condition concerns, pet hair, stains, schedule notes…" />
            </div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 mt-2">
              <button
                type="submit"
                data-testid="request-submit-button"
                disabled={sending}
                className="flex-1 font-accent text-xs tracking-[0.25em] uppercase bg-volt text-white px-9 py-4 hover:bg-[#4A8FFF] disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? "Sending…" : "Request Service"}
              </button>
              <a
                data-testid="text-dark-now"
                href="sms:+19133885449"
                className="flex-1 inline-flex items-center justify-center gap-3 font-accent text-xs tracking-[0.25em] uppercase border border-silver/25 text-silver px-9 py-4 hover:border-volt hover:text-ivory transition-colors duration-300"
              >
                <MessageSquareText className="w-4 h-4" /> Text DARK Now
              </a>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
